import { Menu, X, Search, ChevronDown, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import LeadCaptureForm from "@/components/home/LeadCaptureForm";
import hticuLogo from "@/assets/hticu-logo.png";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Container } from "@/components/layout/Container";
import { ApplicationDialog } from "@/components/application/ApplicationDialog";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { useApplicantCheck } from "@/hooks/use-applicant-check";
import { useAdvisorCheck } from "@/hooks/use-advisor-check";
import { useAdminCheck } from "@/hooks/use-admin-check";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavigationProps {
  onCommandOpen?: () => void;
}

const Navigation = ({ onCommandOpen }: NavigationProps = {}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { isApplicant } = useApplicantCheck();
  const { data: advisorCheck, isLoading: advisorLoading } = useAdvisorCheck();
  const { data: adminCheck, isLoading: adminLoading } = useAdminCheck();

  // Debug logging
  useEffect(() => {
    console.log('🔐 Navigation Role Check:', {
      adminCheck,
      adminLoading,
      advisorCheck,
      advisorLoading,
      isApplicant
    });
  }, [adminCheck, advisorCheck, isApplicant, adminLoading, advisorLoading]);

  // Track scroll position for enhanced navigation styling
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const academicsLinks = [
    { to: "/programs", label: "Programs" },
    { to: "/library", label: "Library" },
    { to: "/admissions", label: "Admissions" },
  ];

  const aboutLinks = [
    { to: "/about", label: "About" },
    { to: "/faculty", label: "Faculty" },
    { to: "/contact", label: "Contact" },
  ];

  const allNavLinks = [
    { to: "/", label: "Home" },
    ...academicsLinks,
    { to: "/tuition", label: "Tuition" },
    ...aboutLinks,
  ];

  const isActive = (path: string) => location.pathname === path;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  return (
    <>
      <ApplicationDialog 
        open={showApplicationDialog} 
        onOpenChange={setShowApplicationDialog} 
      />
      
      <Dialog open={showLeadForm} onOpenChange={setShowLeadForm}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Request Information</DialogTitle>
          </DialogHeader>
          <LeadCaptureForm />
        </DialogContent>
      </Dialog>

      <nav className={`sticky top-0 z-50 w-full transition-smooth border-b ${
        scrolled 
          ? "glass shadow-medium" 
          : "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-soft"
      }`} aria-label="Main navigation">
        <Container>
          <div className="flex h-16 sm:h-20 items-center justify-between gap-4 py-2">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3 transition-smooth hover:opacity-80 min-h-touch py-2"
              aria-label="HTICU Home"
            >
              <img 
                src={hticuLogo} 
                alt="HTICU Logo" 
                className="h-12 sm:h-14 w-auto" 
              />
              <div className="hidden sm:block">
                <div className="text-base sm:text-lg font-serif font-bold text-foreground leading-tight">HTICU</div>
                <div className="text-xs text-muted-foreground hidden lg:block leading-tight mt-0.5">
                  Harvest Time International Christian University
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden lg:flex" aria-label="Main navigation menu">
              <NavigationMenuList>
                {/* Home */}
                <NavigationMenuItem>
                  <Link to="/">
                    <NavigationMenuLink 
                      className={navigationMenuTriggerStyle()}
                      aria-current={isActive("/") ? "page" : undefined}
                    >
                      Home
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                {/* Academics Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>Academics</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[200px] gap-1 p-2">
                      {academicsLinks.map((link) => (
                        <li key={link.to}>
                          <Link to={link.to}>
                            <NavigationMenuLink 
                              className={`block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                                isActive(link.to) ? "bg-primary/10 text-primary" : ""
                              }`}
                              aria-current={isActive(link.to) ? "page" : undefined}
                            >
                              <div className="text-sm font-medium leading-none">{link.label}</div>
                            </NavigationMenuLink>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Tuition */}
                <NavigationMenuItem>
                  <Link to="/tuition">
                    <NavigationMenuLink 
                      className={navigationMenuTriggerStyle()}
                      aria-current={isActive("/tuition") ? "page" : undefined}
                    >
                      Tuition
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                {/* About Us Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger>About Us</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[200px] gap-1 p-2">
                      {aboutLinks.map((link) => (
                        <li key={link.to}>
                          <Link to={link.to}>
                            <NavigationMenuLink 
                              className={`block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                                isActive(link.to) ? "bg-primary/10 text-primary" : ""
                              }`}
                              aria-current={isActive(link.to) ? "page" : undefined}
                            >
                              <div className="text-sm font-medium leading-none">{link.label}</div>
                            </NavigationMenuLink>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Desktop CTAs */}
            <div className="hidden md:flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onCommandOpen}
                className="transition-smooth hover-lift"
                title="Search (Ctrl+K)"
                aria-label="Search"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </Button>
              {(adminCheck?.isAdmin || advisorCheck?.isAdvisor || isApplicant) && (
                <NotificationBell />
              )}
              <ThemeToggle />
              
              {/* Portal Dropdown - Show only if user has portal access */}
              {(adminCheck?.isAdmin || advisorCheck?.isAdvisor || isApplicant) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="min-h-touch gap-1"
                    >
                      <User className="h-4 w-4" />
                      Portal
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-popover z-50">
                    {adminCheck?.isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          Admin Portal
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {advisorCheck?.isAdvisor && (
                      <DropdownMenuItem asChild>
                        <Link to="/advisor/dashboard" className="cursor-pointer">
                          Advisors Portal
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {isApplicant && (
                      <DropdownMenuItem asChild>
                        <Link to="/portal" className="cursor-pointer">
                          Applicant Portal
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {!adminCheck?.isAdmin && !advisorCheck?.isAdvisor && !isApplicant && (
                      <DropdownMenuItem disabled>
                        <span className="text-muted-foreground">No portal access</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Button
                variant="outline"
                size="sm"
                asChild
                className="min-h-touch"
                aria-label="Sign in to your account"
              >
                <Link to="/auth/login">
                  Sign In
                </Link>
              </Button>
              <Button 
                size="sm" 
                className="bg-primary hover:bg-primary-hover min-h-touch"
                onClick={() => setShowApplicationDialog(true)}
                aria-label="Start application process"
              >
                Apply Now
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden min-h-touch min-w-touch"
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Menu className="h-6 w-6" aria-hidden="true" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Navigation Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-2 mt-6" aria-label="Mobile navigation">
                  {allNavLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-3 text-base font-medium rounded-md transition-smooth min-h-touch flex items-center ${
                        isActive(link.to)
                          ? "text-primary bg-primary/5"
                          : "text-foreground/80 hover:text-primary hover:bg-muted"
                      }`}
                      aria-current={isActive(link.to) ? "page" : undefined}
                    >
                      {link.label}
                    </Link>
                  ))}
                  
                  {/* Portal Links */}
                  {(adminCheck?.isAdmin || advisorCheck?.isAdvisor || isApplicant) && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Portals
                      </div>
                      {adminCheck?.isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-3 text-base font-medium rounded-md transition-smooth min-h-touch flex items-center text-foreground/80 hover:text-primary hover:bg-muted"
                        >
                          Admin Portal
                        </Link>
                      )}
                      {advisorCheck?.isAdvisor && (
                        <Link
                          to="/advisor/dashboard"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-3 text-base font-medium rounded-md transition-smooth min-h-touch flex items-center text-foreground/80 hover:text-primary hover:bg-muted"
                        >
                          Advisors Portal
                        </Link>
                      )}
                      {isApplicant && (
                        <Link
                          to="/portal"
                          onClick={() => setMobileMenuOpen(false)}
                          className="px-4 py-3 text-base font-medium rounded-md transition-smooth min-h-touch flex items-center text-foreground/80 hover:text-primary hover:bg-muted"
                        >
                          Applicant Portal
                        </Link>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-3 mt-6 pt-6 border-t">
                    <div className="flex justify-center pb-4">
                      <ThemeToggle />
                    </div>
                    <Button
                      variant="outline"
                      size="lg"
                      asChild
                      className="w-full min-h-touch"
                    >
                      <Link to="/auth/login" onClick={() => setMobileMenuOpen(false)}>
                        Sign In
                      </Link>
                    </Button>
                    <Button 
                      size="lg" 
                      className="w-full bg-primary hover:bg-primary-hover min-h-touch"
                      onClick={() => {
                        setShowApplicationDialog(true);
                        setMobileMenuOpen(false);
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </Container>
      </nav>
    </>
  );
};

export default Navigation;
